import type { TOC } from '@ember/component/template-only';
import { array, hash } from '@ember/helper';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import {
  PrismLoadingBar,
  PrismModal,
  PrismTabsContainer,
} from '@mylighthouse/prism-ember';
import perform from 'ember-concurrency/helpers/perform';
import { t } from 'ember-intl';
import GeneralButtonsButtonExport from 'lumen/components/general/buttons/button-export';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import HotelStrategyLeadtimeTabsTabGraph from 'lumen/components/hotel/strategy/leadtime/tabs/tab-graph';
import HotelStrategyTablesTableDetailLeadtime from 'lumen/components/hotel/strategy/tables/table-detail-leadtime';
import type HotelStrategyLeadtimeController from 'lumen/controllers/hotel/strategy/leadtime';
import { DETAIL_VIEW_TABS } from 'lumen/utils/constants/rate-strategy-constants';

interface HotelStrategyLeadtimeSignature {
  Args: {
    controller: HotelStrategyLeadtimeController;
    model: unknown;
  };
}

<template>
  {{! @glint-nocheck: Need to convert controller to *.ts }}
  {{! template-lint-disable no-duplicate-id }}
  <PrismModal
    @onClose={{@controller.redirectOnClose}}
    @title={{t "strategy.block_title.leadtime_pattern"}}
    {{didInsert @controller.resetContainerDimensions}}
  >
    <:actions>
      <div class="flex items-center gap-400">
        <GeneralIconWithTooltip
          @color="default-inverse"
          @side="top"
          @tooltipContent={{t
            "strategy.block_title_explanation.leadtime_pattern"
          }}
        />
        <GeneralButtonsButtonExport
          @exportAction={{perform @controller.exportRSTableTask}}
          data-event-id="hotel-rate-strategy-detail-export"
          data-event-value={{@controller.router.currentRoute.localName}}
        />
      </div>
    </:actions>

    <:default>
      <div class="rate-strategy-leadtime-modal modal--stretched">
        {{#if @controller.isLoading}}
          <PrismLoadingBar @isFixed={{true}} @positionTop={{60}} />
        {{else if @controller.canRender}}
          <PrismTabsContainer
            @activeTabId={{@controller.tab}}
            @onSelectTab={{@controller.makeActiveTab}}
            @visibleTabIds={{array
              DETAIL_VIEW_TABS.GRAPH
              DETAIL_VIEW_TABS.TABLE
            }}
            as |TabsContainer|
          >
            <TabsContainer.header as |Header|>
              <Header.tab @id={{DETAIL_VIEW_TABS.GRAPH}}>
                {{t "general.graph"}}
              </Header.tab>
              <Header.tab @id={{DETAIL_VIEW_TABS.TABLE}}>
                {{t "general.table"}}
              </Header.tab>
            </TabsContainer.header>
            <TabsContainer.content as |Content|>
              <Content.tab @id={{DETAIL_VIEW_TABS.GRAPH}}>
                <HotelStrategyLeadtimeTabsTabGraph
                  @params={{hash
                    hasRSData=@controller.hasRSData
                    height=@controller.height
                    leadtimeDataPerHotelInfo=@controller.leadtimeDataPerHotelInfo
                    sortBy=@controller.sortBy
                    sortDirection=@controller.sortDirection
                    sortTableData=@controller.sortTableData
                    subtypes=@controller.subtypes
                    typeData=@controller.typeData
                    width=@controller.width
                  }}
                />
              </Content.tab>
              <Content.tab @id={{DETAIL_VIEW_TABS.TABLE}}>
                <HotelStrategyTablesTableDetailLeadtime
                  @initialSorts={{@controller.initialSorts}}
                  @leadtimeDataPerHotelInfo={{@controller.leadtimeDataPerHotelInfo}}
                  @onTableSort={{@controller.onTableSort}}
                  @sortFunction={{@controller.sortFunction}}
                  @subtypes={{@controller.subtypes}}
                />
              </Content.tab>
            </TabsContainer.content>
          </PrismTabsContainer>
        {{/if}}
      </div>
    </:default>
  </PrismModal>
</template> satisfies TOC<HotelStrategyLeadtimeSignature>;
